import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronRight,
  Clock3,
  Database,
  Layers3,
  Plus,
  RefreshCw,
  Trash2,
  User,
} from "lucide-react";
import { Context } from "../../context/Context";
import {
  Button,
  Card,
  Input,
  NoticeBox,
  SectionHeader,
  Select,
  Tags,
} from "../ui";
import { fetchLogsAPI } from "../../utils/api/logs";
import { LogDetails } from "./LogDetails";
import { LogPagination } from "./LogPagination";
import {
  actionCategory,
  actorDetails,
  entityIdOf,
  formatDate,
  humanize,
  readableAction,
  relativeTime,
} from "./logFormatters";
import "./Logs.css";

const LIMIT = 50;
const iconFor = (category) =>
  category === "create"
    ? Plus
    : category === "login"
      ? User
      : category === "delete"
        ? Trash2
        : category === "bulk"
          ? Layers3
          : Database;
const tagVariantFor = (category) =>
  ({ update: "primary", create: "success", delete: "danger", bulk: "warning" })[
    category
  ] || "neutral";

const EMPTY_FILTER_OPTIONS = {
  actions: new Map(),
  entities: new Map(),
  users: new Map(),
};

// Remember every filter value seen so far. Building the dropdowns from the
// current page alone made them collapse to the selected value once a filter
// was applied, so the user couldn't switch to a different one.
const mergeFilterOptions = (previous, logs) => {
  const next = {
    actions: new Map(previous.actions),
    entities: new Map(previous.entities),
    users: new Map(previous.users),
  };
  logs.forEach((log) => {
    if (log.action) next.actions.set(log.action, readableAction(log.action));
    if (log.entityType)
      next.entities.set(log.entityType, humanize(log.entityType));
    if (log.userId && typeof log.userId === "object" && log.userId._id) {
      const user = actorDetails(log.userId);
      next.users.set(
        log.userId._id,
        `${user.name}${user.email ? ` — ${user.email}` : ""}`,
      );
    }
  });
  return next;
};

const sortedOptions = (map) =>
  [...map.keys()].sort().map((value) => ({ label: map.get(value), value }));

export const Logs = () => {
  const { lang, handleTokenExpiration } = useContext(Context);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: LIMIT,
    total: 0,
    pages: 1,
  });
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [userId, setUserId] = useState("");
  const [search, setSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [requestKey, setRequestKey] = useState(0);
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const resultsRef = useRef(null);

  // The context recreates handleTokenExpiration on every render. Reading it
  // through a ref keeps it out of fetchLogs' dependencies, so unrelated
  // context updates don't abort and refetch the current page.
  const tokenExpirationRef = useRef(handleTokenExpiration);
  useEffect(() => {
    tokenExpirationRef.current = handleTokenExpiration;
  });

  const fetchLogs = useCallback(
    async (signal) => {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        tokenExpirationRef.current();
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(false);
      try {
        const result = await fetchLogsAPI(
          token,
          action,
          entityType,
          userId,
          page,
          LIMIT,
          signal,
        );
        // A newer request replaced this one; it must not overwrite state.
        if (signal.aborted) return;
        if (!result?.logs) {
          setError(true);
          setLogs([]);
          return;
        }
        setLogs(result.logs);
        setFilterOptions((previous) =>
          mergeFilterOptions(previous, result.logs),
        );
        setPagination(
          result.pagination || {
            page,
            limit: LIMIT,
            total: result.logs.length,
            pages: 1,
          },
        );
      } catch (requestError) {
        if (!signal.aborted && requestError.name !== "AbortError") {
          setError(true);
          setLogs([]);
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [action, entityType, userId, page],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchLogs(controller.signal);
    return () => controller.abort();
  }, [fetchLogs, requestKey]);

  const actionOptions = useMemo(
    () => sortedOptions(filterOptions.actions),
    [filterOptions.actions],
  );
  const entityOptions = useMemo(
    () => sortedOptions(filterOptions.entities),
    [filterOptions.entities],
  );
  const userOptions = useMemo(
    () =>
      [...filterOptions.users.entries()].map(([value, label]) => ({
        value,
        label,
      })),
    [filterOptions.users],
  );

  // Everything a row shows (and search matches against) is derived once per
  // fetch instead of on every render and every search keystroke.
  const rows = useMemo(
    () =>
      logs.map((log, index) => {
        const actor = actorDetails(log.userId);
        const entityId = entityIdOf(log);
        return {
          key: log._id || `${log.createdAt}-${index}`,
          log,
          actor,
          entityId,
          category: actionCategory(log.action),
          title: readableAction(log.action, log.entityType),
          entityLabel: humanize(log.entityType) || "Unknown entity",
          date: formatDate(log.createdAt, lang),
          relative: relativeTime(log.createdAt, lang),
          searchValues: [
            actor.name,
            actor.email,
            log.action,
            log.entityType,
            entityId,
          ].map((value) => String(value || "").toLowerCase()),
        };
      }),
    [logs, lang],
  );
  const displayedRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      row.searchValues.some((value) => value.includes(query)),
    );
  }, [rows, search]);

  const hasFilters = Boolean(action || entityType || userId || search);
  const resetFilters = () => {
    setAction("");
    setEntityType("");
    setUserId("");
    setSearch("");
    setPage(1);
  };
  const changeServerFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };
  const changePage = (nextPage) => {
    setPage(nextPage);
    // Pagination sits below the list; bring the new page's first rows into view.
    resultsRef.current?.scrollIntoView({ block: "start" });
  };
  const closeDetails = useCallback(() => setSelectedLog(null), []);

  return (
    <div className="audit-page" dir={lang === "ar" ? "rtl" : "ltr"}>
      <SectionHeader title={lang === "ar" ? "سجل النشاط" : "Audit history"} />
      <section className="audit-toolbar" aria-label="Audit log filters">
        <Card hoverable={false}>
          <div className="audit-filter-grid">
            <Input
              fullWidth={false}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search this page…"
              aria-label="Search loaded audit logs"
            />
            <div role="group" aria-label="Filter by action">
              <Select
                value={action}
                onChange={changeServerFilter(setAction)}
                placeholder="All actions"
                options={[
                  { label: "All actions", value: "" },
                  ...actionOptions,
                ]}
              />
            </div>
            <div role="group" aria-label="Filter by entity type">
              <Select
                value={entityType}
                onChange={changeServerFilter(setEntityType)}
                placeholder="All entity types"
                options={[
                  { label: "All entity types", value: "" },
                  ...entityOptions,
                ]}
              />
            </div>
            <div role="group" aria-label="Filter by user">
              <Select
                value={userId}
                onChange={changeServerFilter(setUserId)}
                placeholder="All users"
                options={[{ label: "All users", value: "" }, ...userOptions]}
              />
            </div>
            <Button
              variant="neutral"
              appearance="outline"
              size="lg"
              fullWidth
              onClick={resetFilters}
              disabled={!hasFilters}
            >
              Reset
            </Button>
          </div>
          <p className="audit-filter-note">
            Search filters the {logs.length} logs on this page. Dropdown filters
            are applied across the audit history.
            {hasFilters && (
              <span className="audit-active-filter"> Filters active</span>
            )}
          </p>
        </Card>
      </section>

      <div ref={resultsRef} className="audit-results-anchor" />
      {loading ? (
        <div
          className="audit-skeleton"
          role="status"
          aria-label="Loading audit logs"
          aria-busy="true"
        >
          {Array.from({ length: 6 }, (_, index) => (
            <div className="audit-skeleton-row" key={index} />
          ))}
        </div>
      ) : error ? (
        <div className="audit-state-wrap">
          <NoticeBox
            title="Audit history could not be loaded"
            variant="danger"
            icon={RefreshCw}
            bordered
            className="audit-state"
          >
            <p>Please check your connection and try again.</p>
            <Button
              variant="danger"
              appearance="outline"
              onClick={() => setRequestKey((key) => key + 1)}
            >
              Retry
            </Button>
          </NoticeBox>
        </div>
      ) : displayedRows.length === 0 ? (
        <div className="audit-state-wrap">
          <NoticeBox
            title={
              hasFilters ? "No logs match these filters" : "No activity yet"
            }
            variant="neutral"
            icon={Clock3}
            bordered
            className="audit-state"
          >
            <p>
              {hasFilters
                ? "Try changing your search or clearing the active filters."
                : "Activity from the last 15 days will appear here."}
            </p>
            {hasFilters && (
              <Button
                variant="neutral"
                appearance="outline"
                onClick={resetFilters}
              >
                Clear filters
              </Button>
            )}
          </NoticeBox>
        </div>
      ) : (
        <div className="audit-list">
          {displayedRows.map((row) => {
            const Icon = iconFor(row.category);
            return (
              <button
                type="button"
                className="audit-row"
                key={row.key}
                onClick={() => setSelectedLog(row.log)}
                aria-label={`View details: ${row.title} by ${row.actor.name}`}
              >
                <span
                  className={`audit-action-icon ${row.category}`}
                  aria-hidden="true"
                >
                  <Icon size={19} />
                </span>
                <span className="audit-actor">
                  <strong>{row.actor.name}</strong>
                  {row.actor.email && <small>{row.actor.email}</small>}
                </span>
                <span className="audit-row-main">
                  <strong>{row.title}</strong>
                  <span className="audit-row-sub">
                    {row.entityId
                      ? `Entity ID: ${row.entityId}`
                      : "View recorded details"}
                  </span>
                </span>
                <span className="audit-row-entity">
                  <Tags variant={tagVariantFor(row.category)} size="sm">
                    {row.category === "bulk" ? "Bulk · " : ""}
                    {row.entityLabel}
                  </Tags>
                </span>
                <span className="audit-time">
                  <strong>{row.date}</strong>
                  <small>{row.relative}</small>
                </span>
                <ChevronRight
                  className="audit-row-chevron"
                  size={18}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
      )}

      {!loading && !error && (
        <LogPagination
          page={pagination.page}
          pages={pagination.pages}
          total={pagination.total}
          limit={pagination.limit}
          visibleCount={displayedRows.length}
          hasSearch={Boolean(search)}
          onPageChange={changePage}
        />
      )}
      {selectedLog && (
        <LogDetails log={selectedLog} lang={lang} onClose={closeDetails} />
      )}
    </div>
  );
};
